import { isFunction } from 'lodash';
import { type MemoExoticComponent, memo, type ReactNode } from 'react';

import { type UseWizard, useWizard } from '../../hooks/useWizard';
import { WizardContext } from './contexts/Wizard.context';
import { WizardStepWithContext } from './step/WizardStepWithContext';

type WizardProps = {
  /**
   * List of names of steps in the wizard, in the order of the steps.
   * Make sure that the reference does not change or it will reset the current step.
   */
  steps: string[];
  /** Current step name. */
  initialStep?: string;
  /** Children. If a function, called with the useWizard return value. */
  children?: (wizardValue: UseWizard) => ReactNode | ReactNode;
};

const defaultSteps: string[] = [];

export const WizardBase = ({
  steps = defaultSteps,
  initialStep = undefined,
  children = undefined,
}: WizardProps) => {
  const wizard = useWizard(steps, initialStep);

  return (
    <WizardContext.Provider value={wizard}>
      {isFunction(children) ? children(wizard) : children}
    </WizardContext.Provider>
  );
};

WizardBase.displayName = 'Wizard';

export const Wizard: MemoExoticComponent<typeof WizardBase> & {
  Step?: typeof WizardStepWithContext;
} = memo(WizardBase);

Wizard.Step = WizardStepWithContext;
